import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'
import type { PrintJob, PrintJobInsert, PrintJobUpdate } from '@/lib/database.types'

export function usePrintJobs() {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()

  const {
    data: printJobs = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['print_jobs', user?.id],
    queryFn: async (): Promise<PrintJob[]> => {
      if (!user?.id) return []
      
      const { data, error } = await supabase
        .from('print_jobs')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
      
      if (error) throw error
      return data || []
    },
    enabled: !!user?.id,
    staleTime: 1000 * 30,
  })

  const createPrintJobMutation = useMutation({
    mutationFn: async (job: Omit<PrintJobInsert, 'user_id'>) => {
      if (!user?.id) throw new Error('Not authenticated')
      
      const { data, error } = await supabase
        .from('print_jobs')
        .insert({ ...job, user_id: user.id })
        .select()
        .single()
      
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print_jobs', user?.id] })
    },
  })

  const updatePrintJobMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: PrintJobUpdate }) => {
      const { data, error } = await supabase
        .from('print_jobs')
        .update(updates)
        .eq('id', id)
        .select()
        .single()
      
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['print_jobs', user?.id] })
    },
  })

  // Computed stats
  const completedJobs = printJobs.filter(j => j.status === 'completed')
  const failedJobs = printJobs.filter(j => j.status === 'failed')
  const activeJobs = printJobs.filter(j => ['printing', 'preparing', 'queued'].includes(j.status))
  const currentJob = printJobs.find(j => j.status === 'printing')
  
  const totalJobs = completedJobs.length + failedJobs.length
  const successRate = totalJobs > 0 
    ? Math.round((completedJobs.length / totalJobs) * 100) 
    : null

  return {
    printJobs,
    activeJobs,
    completedJobs,
    failedJobs,
    currentJob,
    successRate,
    totalJobs,
    isLoading,
    error,
    refetch,
    createPrintJob: createPrintJobMutation.mutateAsync,
    updatePrintJob: updatePrintJobMutation.mutateAsync,
    isCreating: createPrintJobMutation.isPending,
    isUpdating: updatePrintJobMutation.isPending,
  }
}
